import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';

// ====== Import schemas from generated ======
const {
  LoyaltySettingsFirestoreResponse,
  UpdateLoyaltySettingsDto,
  MarketplaceSettingsFirestoreResponse,
  UpdateMarketplaceSettingsDto,
  UpsertOrganizationConstantDto,
  OrganizationConstantResponseDto,
} = schemas;

// ====== Re-export schemas for external use ======
export const LoyaltySettingsResponseSchema = LoyaltySettingsFirestoreResponse;
export const UpdateLoyaltySettingsSchema = UpdateLoyaltySettingsDto;
export const MarketplaceSettingsResponseSchema = MarketplaceSettingsFirestoreResponse;
export const UpdateMarketplaceSettingsSchema = UpdateMarketplaceSettingsDto;
export const UpsertOrganizationConstantSchema = UpsertOrganizationConstantDto;
export const OrganizationConstantResponseSchema = OrganizationConstantResponseDto;

// Type exports
export type LoyaltySettingsResponse = z.infer<typeof LoyaltySettingsResponseSchema>;
export type MarketplaceSettingsResponse = z.infer<typeof MarketplaceSettingsResponseSchema>;
export type UpdateLoyaltySettings = z.infer<typeof UpdateLoyaltySettingsSchema>;
export type UpdateMarketplaceSettings = z.infer<typeof UpdateMarketplaceSettingsSchema>;
export type OrganizationConstantResponse = z.infer<typeof OrganizationConstantResponseSchema>;

/**
 * ATL-1038 — the flat `constants/<key>` documents that live one verbatim row each in
 * `organization_constants`. The key is an enum on the wire, so a typo is a compile error here
 * rather than a 400 nobody sees on a fire-and-forget settings toggle.
 */
export type OrganizationConstantKey = OrganizationConstantResponse['key'];

// API factory function
export function createSettingsApi(client: TypedApiClient) {
  return {
    getLoyaltySettings: (): Promise<LoyaltySettingsResponse> => {
      return client.get('/loyalty-settings', {
        responseSchema: LoyaltySettingsResponseSchema,
      });
    },

    updateLoyaltySettings: (data: UpdateLoyaltySettings): Promise<LoyaltySettingsResponse> => {
      return client.patch('/loyalty-settings', {
        data,
        responseSchema: LoyaltySettingsResponseSchema,
      });
    },

    getMarketplaceSettings: (): Promise<MarketplaceSettingsResponse> => {
      return client.get('/marketplace-settings', {
        responseSchema: MarketplaceSettingsResponseSchema,
      });
    },

    updateMarketplaceSettings: (data: UpdateMarketplaceSettings): Promise<MarketplaceSettingsResponse> => {
      return client.patch('/marketplace-settings', {
        data,
        responseSchema: MarketplaceSettingsResponseSchema,
      });
    },

    /**
     * Store one flat `constants/<key>` document VERBATIM (ATL-1038).
     *
     * `document` is the whole post-write Firestore document, not a patch: the fleet does not agree
     * on its shape (`printChecks` carries printer geometry on two orgs), and sending the document
     * is what keeps the two stores byte-identical rather than agreeing only on the fields this
     * build happens to know about.
     */
    upsertOrganizationConstant: (
      key: OrganizationConstantKey,
      document: Record<string, unknown>,
    ): Promise<OrganizationConstantResponse> => {
      return client.put(apiPath`/settings/constants/${key}`, {
        data: { document },
        responseSchema: OrganizationConstantResponseSchema,
      });
    },
  };
}
